import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Settings, MessageSquare, Link2, Home, LogOut, Trash2, 
  Edit, Eye, EyeOff, Loader2, Save, Check, X, Globe, ChevronLeft,
  Image, Upload, DollarSign, ExternalLink, MousePointer, Languages, ArrowRightLeft, Reply, Send, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('articles');
  const fileInputRef = useRef(null);
  
  // Data states
  const [articles, setArticles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [affiliateLinks, setAffiliateLinks] = useState([]);
  const [settings, setSettings] = useState({});
  const [pages, setPages] = useState([]);
  const [heroImageInfo, setHeroImageInfo] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  
  // Form states
  const [articleForm, setArticleForm] = useState({
    title_fr: '', title_de: '', slug: '', content_fr: '', content_de: '',
    excerpt_fr: '', excerpt_de: '', image_url: '', category: 'blog', published: false
  });
  const [editingArticle, setEditingArticle] = useState(null);
  const [socialForm, setSocialForm] = useState({ platform: '', url: '', icon: '', active: true });
  const [editingSocial, setEditingSocial] = useState(null);
  const [affiliateForm, setAffiliateForm] = useState({ 
    name: '', description_fr: '', description_de: '', url: '', image_url: '', 
    category: 'general', commission: '', active: true 
  });
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  
  // Page editing
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageForm, setPageForm] = useState({ title_fr: '', title_de: '', content_fr: '', content_de: '' });
  const [savingPage, setSavingPage] = useState(false);

  // Translation
  const [translating, setTranslating] = useState(false);
  const [translateField, setTranslateField] = useState('');

  // Article image upload
  const articleImageRef = useRef(null);
  const [uploadingArticleImage, setUploadingArticleImage] = useState(false);

  // Auth header
  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { headers: { Authorization: `Basic ${auth}` } };
  };

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    setLoading(true);
    try {
      const [articlesRes, messagesRes, linksRes, affiliateRes, settingsRes, pagesRes, heroRes] = await Promise.all([
        axios.get(`${API}/admin/articles`, authHeader),
        axios.get(`${API}/admin/messages`, authHeader),
        axios.get(`${API}/admin/social-links`, authHeader),
        axios.get(`${API}/admin/affiliate-links`, authHeader),
        axios.get(`${API}/admin/settings`, authHeader),
        axios.get(`${API}/pages`),
        axios.get(`${API}/admin/hero-image-info`, authHeader)
      ]);
      
      setArticles(articlesRes.data);
      setMessages(messagesRes.data);
      setSocialLinks(linksRes.data);
      setAffiliateLinks(affiliateRes.data);
      setSettings(settingsRes.data);
      setPages(pagesRes.data);
      setHeroImageInfo(heroRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  // ===================== DEEPL TRANSLATION =====================
  const translateText = async (text, sourceLang, targetLang) => {
    const authHeader = getAuthHeader();
    if (!authHeader || !text) return null;

    try {
      const response = await axios.post(`${API}/admin/translate`, {
        text,
        source_lang: sourceLang,
        target_lang: targetLang
      }, authHeader);
      return response.data.translated_text;
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t("Clé API DeepL non configurée. Ajoutez-la dans les Paramètres.", "DeepL API-Schlüssel nicht konfiguriert."));
      } else {
        toast.error(t("Erreur de traduction", "Übersetzungsfehler"));
      }
      return null;
    }
  };

  const handleTranslateArticle = async (field, direction) => {
    setTranslating(true);
    setTranslateField(field);
    
    const sourceLang = direction === 'fr_to_de' ? 'FR' : 'DE';
    const targetLang = direction === 'fr_to_de' ? 'DE' : 'FR';
    const sourceField = direction === 'fr_to_de' ? `${field}_fr` : `${field}_de`;
    const targetField = direction === 'fr_to_de' ? `${field}_de` : `${field}_fr`;
    
    const translated = await translateText(articleForm[sourceField], sourceLang, targetLang);
    if (translated) {
      setArticleForm(prev => ({ ...prev, [targetField]: translated }));
      toast.success(t('Traduction effectuée !', 'Übersetzung abgeschlossen!'));
    }
    setTranslating(false);
    setTranslateField('');
  };

  const handleTranslatePage = async (direction) => {
    if (!pageForm.content_fr && direction === 'fr_to_de') {
      toast.error(t('Aucun contenu français à traduire', 'Kein französischer Inhalt zum Übersetzen'));
      return;
    }
    if (!pageForm.content_de && direction === 'de_to_fr') {
      toast.error(t('Aucun contenu allemand à traduire', 'Kein deutscher Inhalt zum Übersetzen'));
      return;
    }

    setTranslating(true);
    const sourceLang = direction === 'fr_to_de' ? 'FR' : 'DE';
    const targetLang = direction === 'fr_to_de' ? 'DE' : 'FR';
    
    // Translate title
    const titleSource = direction === 'fr_to_de' ? pageForm.title_fr : pageForm.title_de;
    const translatedTitle = await translateText(titleSource, sourceLang, targetLang);
    
    // Translate content
    const contentSource = direction === 'fr_to_de' ? pageForm.content_fr : pageForm.content_de;
    const translatedContent = await translateText(contentSource, sourceLang, targetLang);
    
    if (translatedTitle && translatedContent) {
      if (direction === 'fr_to_de') {
        setPageForm(prev => ({ ...prev, title_de: translatedTitle, content_de: translatedContent }));
      } else {
        setPageForm(prev => ({ ...prev, title_fr: translatedTitle, content_fr: translatedContent }));
      }
      toast.success(t('Page traduite !', 'Seite übersetzt!'));
    }
    setTranslating(false);
  };

  // ===================== HERO IMAGE UPLOAD =====================
  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const authHeader = getAuthHeader();
    if (!authHeader) return;

    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/admin/upload-hero-image`, formData, {
        ...authHeader,
        headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t("Image d'accueil mise à jour !", "Hero-Bild aktualisiert!"));
      fetchAllData();
    } catch (error) {
      toast.error(t("Erreur lors de l'upload", "Fehler beim Hochladen"));
    } finally {
      setUploadingHero(false);
    }
  };

  // ===================== ARTICLE IMAGE UPLOAD =====================
  const handleArticleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const authHeader = getAuthHeader();
    if (!authHeader) return;

    setUploadingArticleImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload-article-image`, formData, {
        ...authHeader,
        headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' }
      });

      // Set the image URL in the article form
      setArticleForm(prev => ({ ...prev, image_url: response.data.url }));
      toast.success(t("Image téléchargée !", "Bild hochgeladen!"));
    } catch (error) {
      toast.error(t("Erreur lors de l'upload", "Fehler beim Hochladen"));
    } finally {
      setUploadingArticleImage(false);
    }
  };

  // ===================== ARTICLES =====================
  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.id}`, articleForm, authHeader);
        toast.success(t('Article mis à jour !', 'Artikel aktualisiert!'));
      } else {
        await axios.post(`${API}/admin/articles`, articleForm, authHeader);
        toast.success(t('Article créé !', 'Artikel erstellt!'));
      }
      resetArticleForm();
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur lors de la sauvegarde', 'Fehler beim Speichern'));
    }
  };

  const resetArticleForm = () => {
    setArticleForm({
      title_fr: '', title_de: '', slug: '', content_fr: '', content_de: '',
      excerpt_fr: '', excerpt_de: '', image_url: '', category: 'blog', published: false
    });
    setEditingArticle(null);
  };

  const editArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title_fr: article.title_fr,
      title_de: article.title_de || '',
      slug: article.slug,
      content_fr: article.content_fr,
      content_de: article.content_de || '',
      excerpt_fr: article.excerpt_fr || '',
      excerpt_de: article.excerpt_de || '',
      image_url: article.image_url || '',
      category: article.category,
      published: article.published
    });
  };

  const deleteArticle = async (id) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, authHeader);
      toast.success(t('Article supprimé', 'Artikel gelöscht'));
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  // ===================== SOCIAL LINKS =====================
  const handleSocialSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      if (editingSocial) {
        await axios.put(`${API}/admin/social-links/${editingSocial.id}`, socialForm, authHeader);
        toast.success(t('Lien mis à jour !', 'Link aktualisiert!'));
      } else {
        await axios.post(`${API}/admin/social-links`, socialForm, authHeader);
        toast.success(t('Lien créé !', 'Link erstellt!'));
      }
      setSocialForm({ platform: '', url: '', icon: '', active: true });
      setEditingSocial(null);
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  const deleteSocialLink = async (id) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    try {
      await axios.delete(`${API}/admin/social-links/${id}`, authHeader);
      toast.success(t('Lien supprimé', 'Link gelöscht'));
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  // ===================== AFFILIATE LINKS =====================
  const handleAffiliateSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      if (editingAffiliate) {
        await axios.put(`${API}/admin/affiliate-links/${editingAffiliate.id}`, affiliateForm, authHeader);
        toast.success(t('Lien affilié mis à jour !', 'Affiliate-Link aktualisiert!'));
      } else {
        await axios.post(`${API}/admin/affiliate-links`, affiliateForm, authHeader);
        toast.success(t('Lien affilié créé !', 'Affiliate-Link erstellt!'));
      }
      setAffiliateForm({ name: '', description_fr: '', description_de: '', url: '', image_url: '', category: 'general', commission: '', active: true });
      setEditingAffiliate(null);
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  const deleteAffiliateLink = async (id) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    try {
      await axios.delete(`${API}/admin/affiliate-links/${id}`, authHeader);
      toast.success(t('Lien supprimé', 'Link gelöscht'));
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  // ===================== MESSAGES =====================
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const markMessageRead = async (id) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    try {
      await axios.put(`${API}/admin/messages/${id}/read`, {}, authHeader);
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  const deleteMessage = async (id) => {
    const authHeader = getAuthHeader();
    if (!authHeader) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, authHeader);
      toast.success(t('Message supprimé', 'Nachricht gelöscht'));
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  const handleReply = async (msg) => {
    if (!replyText.trim()) {
      toast.error(t('Veuillez écrire un message', 'Bitte schreiben Sie eine Nachricht'));
      return;
    }

    const authHeader = getAuthHeader();
    if (!authHeader) return;

    setSendingReply(true);
    try {
      const response = await axios.post(`${API}/admin/messages/${msg.id}/reply`, {
        reply_message: replyText
      }, authHeader);

      // Open mailto link to send the email
      window.open(response.data.mailto_link, '_blank');
      
      toast.success(t('Réponse préparée ! Votre client email va s\'ouvrir.', 'Antwort vorbereitet! Ihr E-Mail-Client wird geöffnet.'));
      setReplyingTo(null);
      setReplyText('');
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur lors de l\'envoi', 'Fehler beim Senden'));
    } finally {
      setSendingReply(false);
    }
  };

  // ===================== PAGES =====================
  const selectPage = (page) => {
    setSelectedPage(page);
    setPageForm({
      title_fr: page.title_fr || '',
      title_de: page.title_de || '',
      content_fr: page.content_fr || '',
      content_de: page.content_de || ''
    });
  };

  const handlePageSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader || !selectedPage) return;

    setSavingPage(true);
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.page_key}`, pageForm, authHeader);
      toast.success(t('Page mise à jour !', 'Seite aktualisiert!'));
      fetchAllData();
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    } finally {
      setSavingPage(false);
    }
  };

  // ===================== SETTINGS =====================
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) return;

    try {
      await axios.put(`${API}/admin/settings`, settings, authHeader);
      toast.success(t('Paramètres sauvegardés !', 'Einstellungen gespeichert!'));
    } catch (error) {
      toast.error(t('Erreur', 'Fehler'));
    }
  };

  const unreadMessages = messages.filter(m => !m.read).length;

  const pageLabels = {
    home: t('Accueil', 'Startseite'),
    about: t('À propos', 'Über mich'),
    mentions: t('Mentions légales', 'Rechtliche Hinweise'),
    privacy: t('Confidentialité', 'Datenschutz'),
    impressum: 'Impressum'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-600 hover:text-blue-600">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-lg font-bold text-slate-900 font-outfit">
                {t('Administration', 'Verwaltung')}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" target="_blank" className="text-slate-600 hover:text-blue-600">
                <Globe className="w-5 h-5" />
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('Déconnexion', 'Abmelden')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white border border-slate-200 p-1 rounded-xl flex-wrap">
            <TabsTrigger value="articles" className="rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="pages" className="rounded-lg">
              <Home className="w-4 h-4 mr-2" />
              Pages
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg relative">
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="social" className="rounded-lg">
              <Link2 className="w-4 h-4 mr-2" />
              {t('Réseaux', 'Soziale')}
            </TabsTrigger>
            <TabsTrigger value="affiliate" className="rounded-lg">
              <DollarSign className="w-4 h-4 mr-2" />
              {t('Affiliation', 'Affiliate')}
            </TabsTrigger>
            <TabsTrigger value="hero" className="rounded-lg">
              <Image className="w-4 h-4 mr-2" />
              Hero
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">
              <Settings className="w-4 h-4 mr-2" />
              {t('Paramètres', 'Einstellungen')}
            </TabsTrigger>
          </TabsList>

          {/* ARTICLES TAB */}
          <TabsContent value="articles">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-bold mb-6 font-outfit">
                    {editingArticle ? t('Modifier', 'Bearbeiten') : t('Nouvel article', 'Neuer Artikel')}
                  </h2>
                  <form onSubmit={handleArticleSubmit} className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>{t('Titre (FR)', 'Titel (FR)')}</Label>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleTranslateArticle('title', 'fr_to_de')} disabled={translating || !articleForm.title_fr}>
                          <Languages className="w-3 h-3 mr-1" />
                          {translating && translateField === 'title' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'FR→DE'}
                        </Button>
                      </div>
                      <Input value={articleForm.title_fr} onChange={(e) => setArticleForm({...articleForm, title_fr: e.target.value})} required />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>{t('Titre (DE)', 'Titel (DE)')}</Label>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleTranslateArticle('title', 'de_to_fr')} disabled={translating || !articleForm.title_de}>
                          <Languages className="w-3 h-3 mr-1" />
                          DE→FR
                        </Button>
                      </div>
                      <Input value={articleForm.title_de} onChange={(e) => setArticleForm({...articleForm, title_de: e.target.value})} />
                    </div>
                    <div>
                      <Label>Slug (URL)</Label>
                      <Input value={articleForm.slug} onChange={(e) => setArticleForm({...articleForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})} required />
                    </div>
                    <div>
                      <Label>{t('Catégorie', 'Kategorie')}</Label>
                      <Select value={articleForm.category} onValueChange={(val) => setArticleForm({...articleForm, category: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blog">Blog</SelectItem>
                          <SelectItem value="experience">{t('Expérience', 'Erfahrung')}</SelectItem>
                          <SelectItem value="tips">{t('Conseils', 'Tipps')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('Image', 'Bild')}</Label>
                      {articleForm.image_url && (
                        <div className="mb-3 relative group">
                          <img 
                            src={articleForm.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${articleForm.image_url}` : articleForm.image_url} 
                            alt="Preview" 
                            className="w-full h-32 object-cover rounded-lg border border-slate-200" 
                          />
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="destructive" 
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setArticleForm({...articleForm, image_url: ''})}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input 
                          type="file" 
                          ref={articleImageRef} 
                          onChange={handleArticleImageUpload} 
                          accept="image/jpeg,image/png,image/webp,image/gif" 
                          className="hidden" 
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => articleImageRef.current?.click()} 
                          disabled={uploadingArticleImage}
                          className="flex-1"
                        >
                          {uploadingArticleImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          {t('Télécharger', 'Hochladen')}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">{t('ou collez une URL:', 'oder URL einfügen:')}</p>
                      <Input 
                        value={articleForm.image_url} 
                        onChange={(e) => setArticleForm({...articleForm, image_url: e.target.value})} 
                        placeholder="https://..." 
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>{t('Contenu (FR)', 'Inhalt (FR)')}</Label>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleTranslateArticle('content', 'fr_to_de')} disabled={translating || !articleForm.content_fr}>
                          <Languages className="w-3 h-3 mr-1" />
                          {translating && translateField === 'content' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'FR→DE'}
                        </Button>
                      </div>
                      <Textarea value={articleForm.content_fr} onChange={(e) => setArticleForm({...articleForm, content_fr: e.target.value})} rows={5} required />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>{t('Contenu (DE)', 'Inhalt (DE)')}</Label>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleTranslateArticle('content', 'de_to_fr')} disabled={translating || !articleForm.content_de}>
                          <Languages className="w-3 h-3 mr-1" />
                          DE→FR
                        </Button>
                      </div>
                      <Textarea value={articleForm.content_de} onChange={(e) => setArticleForm({...articleForm, content_de: e.target.value})} rows={5} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>{t('Publié', 'Veröffentlicht')}</Label>
                      <Switch checked={articleForm.published} onCheckedChange={(checked) => setArticleForm({...articleForm, published: checked})} />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1">
                        <Save className="w-4 h-4 mr-2" />
                        {editingArticle ? t('Mettre à jour', 'Aktualisieren') : t('Créer', 'Erstellen')}
                      </Button>
                      {editingArticle && (
                        <Button type="button" variant="outline" onClick={resetArticleForm}><X className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-bold mb-6 font-outfit">{t('Articles', 'Artikel')} ({articles.length})</h2>
                  <div className="space-y-4">
                    {articles.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">{t('Aucun article', 'Keine Artikel')}</p>
                    ) : (
                      articles.map((article) => (
                        <div key={article.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg" data-testid={`article-item-${article.id}`}>
                          {article.image_url && (
                            <img 
                              src={article.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${article.image_url}` : article.image_url} 
                              alt="" 
                              className="w-16 h-16 object-cover rounded-lg" 
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium truncate">{article.title_fr}</h3>
                              {article.published ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                            </div>
                            <p className="text-sm text-slate-500">{article.category}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => editArticle(article)} data-testid={`edit-article-${article.id}`}><Edit className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteArticle(article.id)} className="text-red-500" data-testid={`delete-article-${article.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* PAGES TAB - SIMPLIFIED */}
          <TabsContent value="pages">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Page List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h2 className="text-lg font-bold mb-4 font-outfit">{t('Pages', 'Seiten')}</h2>
                  <div className="space-y-2">
                    {pages.map((page) => (
                      <button
                        key={page.page_key}
                        onClick={() => selectPage(page)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                          selectedPage?.page_key === page.page_key
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <span className="font-medium">{pageLabels[page.page_key] || page.page_key}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Page Editor */}
              <div className="lg:col-span-3">
                {selectedPage ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold font-outfit">
                        {t('Modifier', 'Bearbeiten')}: {pageLabels[selectedPage.page_key] || selectedPage.page_key}
                      </h2>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => handleTranslatePage('fr_to_de')} disabled={translating}>
                          <Languages className="w-4 h-4 mr-2" />
                          {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'FR → DE'}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => handleTranslatePage('de_to_fr')} disabled={translating}>
                          <Languages className="w-4 h-4 mr-2" />
                          DE → FR
                        </Button>
                      </div>
                    </div>
                    <form onSubmit={handlePageSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-base font-semibold mb-2 block">{t('Français', 'Französisch')} 🇫🇷</Label>
                          <div className="space-y-4">
                            <div>
                              <Label>{t('Titre', 'Titel')}</Label>
                              <Input value={pageForm.title_fr} onChange={(e) => setPageForm({...pageForm, title_fr: e.target.value})} />
                            </div>
                            <div>
                              <Label>{t('Contenu', 'Inhalt')}</Label>
                              <Textarea value={pageForm.content_fr} onChange={(e) => setPageForm({...pageForm, content_fr: e.target.value})} rows={12} className="font-mono text-sm" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-base font-semibold mb-2 block">{t('Allemand', 'Deutsch')} 🇩🇪</Label>
                          <div className="space-y-4">
                            <div>
                              <Label>{t('Titre', 'Titel')}</Label>
                              <Input value={pageForm.title_de} onChange={(e) => setPageForm({...pageForm, title_de: e.target.value})} />
                            </div>
                            <div>
                              <Label>{t('Contenu', 'Inhalt')}</Label>
                              <Textarea value={pageForm.content_de} onChange={(e) => setPageForm({...pageForm, content_de: e.target.value})} rows={12} className="font-mono text-sm" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button type="submit" disabled={savingPage} className="w-full">
                        {savingPage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {t('Sauvegarder', 'Speichern')}
                      </Button>
                    </form>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                    <Home className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('Sélectionnez une page à modifier', 'Wählen Sie eine Seite zum Bearbeiten')}</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold font-outfit">Messages ({messages.length})</h2>
                {unreadMessages > 0 && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    {unreadMessages} {t('non lu(s)', 'ungelesen')}
                  </span>
                )}
              </div>
              <div className="space-y-4" data-testid="messages-list">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('Aucun message', 'Keine Nachrichten')}</p>
                    <p className="text-sm text-slate-400 mt-1">{t('Les messages du formulaire de contact apparaîtront ici', 'Kontaktformular-Nachrichten erscheinen hier')}</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`p-4 rounded-lg border transition-all ${msg.read ? 'bg-slate-50 border-slate-200' : 'bg-blue-50 border-blue-200 shadow-sm'} ${msg.replied ? 'border-l-4 border-l-green-500' : ''}`}
                      data-testid={`message-item-${msg.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${msg.replied ? 'bg-green-500' : msg.read ? 'bg-slate-400' : 'bg-blue-500'}`}>
                            {msg.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900">{msg.name}</h3>
                              {msg.replied && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                  {t('Répondu', 'Beantwortet')}
                                </span>
                              )}
                            </div>
                            <a href={`mailto:${msg.email}`} className="text-sm text-blue-600 hover:underline">{msg.email}</a>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => {setReplyingTo(replyingTo?.id === msg.id ? null : msg); setReplyText('');}}
                            data-testid={`reply-message-${msg.id}`}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            {t('Répondre', 'Antworten')}
                          </Button>
                          {!msg.read && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => markMessageRead(msg.id)}
                              data-testid={`mark-read-${msg.id}`}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              {t('Lu', 'Gelesen')}
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => deleteMessage(msg.id)} 
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-message-${msg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="pl-13 ml-13">
                        <p className="text-slate-700 whitespace-pre-wrap bg-white p-3 rounded-lg border border-slate-100">{msg.message}</p>
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                          <span>•</span>
                          <span>{new Date(msg.created_at).toLocaleTimeString()}</span>
                        </p>
                        
                        {/* Reply Form */}
                        {replyingTo?.id === msg.id && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 animate-fadeIn">
                            <div className="flex items-center gap-2 mb-3">
                              <Mail className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800">
                                {t('Répondre à', 'Antworten an')} {msg.name}
                              </span>
                            </div>
                            <Textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder={t('Écrivez votre réponse...', 'Schreiben Sie Ihre Antwort...')}
                              rows={4}
                              className="mb-3 bg-white"
                              data-testid="reply-textarea"
                            />
                            <div className="flex gap-2">
                              <Button 
                                onClick={() => handleReply(msg)} 
                                disabled={sendingReply || !replyText.trim()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="send-reply-btn"
                              >
                                {sendingReply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                {t('Envoyer', 'Senden')}
                              </Button>
                              <Button 
                                variant="outline" 
                                onClick={() => {setReplyingTo(null); setReplyText('');}}
                              >
                                {t('Annuler', 'Abbrechen')}
                              </Button>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              {t('Votre client email s\'ouvrira pour envoyer le message.', 'Ihr E-Mail-Client wird geöffnet, um die Nachricht zu senden.')}
                            </p>
                          </div>
                        )}
                        
                        {/* Show previous reply if exists */}
                        {msg.replied && msg.reply_text && !replyingTo && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-xs font-medium text-green-700">
                                {t('Votre réponse', 'Ihre Antwort')} ({msg.replied_at ? new Date(msg.replied_at).toLocaleDateString() : ''})
                              </span>
                            </div>
                            <p className="text-sm text-green-800 whitespace-pre-wrap">{msg.reply_text}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* SOCIAL TAB */}
          <TabsContent value="social">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-bold mb-6 font-outfit">{editingSocial ? t('Modifier', 'Bearbeiten') : t('Nouveau lien', 'Neuer Link')}</h2>
                <form onSubmit={handleSocialSubmit} className="space-y-4">
                  <div>
                    <Label>{t('Plateforme', 'Plattform')}</Label>
                    <Input value={socialForm.platform} onChange={(e) => setSocialForm({...socialForm, platform: e.target.value})} placeholder="Instagram, Facebook..." required />
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input value={socialForm.url} onChange={(e) => setSocialForm({...socialForm, url: e.target.value})} placeholder="https://..." required />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>{t('Actif', 'Aktiv')}</Label>
                    <Switch checked={socialForm.active} onCheckedChange={(checked) => setSocialForm({...socialForm, active: checked})} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1"><Save className="w-4 h-4 mr-2" />{editingSocial ? t('Mettre à jour', 'Aktualisieren') : t('Ajouter', 'Hinzufügen')}</Button>
                    {editingSocial && <Button type="button" variant="outline" onClick={() => {setSocialForm({ platform: '', url: '', icon: '', active: true }); setEditingSocial(null);}}><X className="w-4 h-4" /></Button>}
                  </div>
                </form>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-bold mb-6 font-outfit">{t('Liens', 'Links')} ({socialLinks.length})</h2>
                <div className="space-y-4">
                  {socialLinks.map((link) => (
                    <div key={link.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-medium">{link.platform}</h3>
                        <p className="text-sm text-slate-500 truncate">{link.url}</p>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs ${link.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                        {link.active ? t('Actif', 'Aktiv') : t('Inactif', 'Inaktiv')}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => {setEditingSocial(link); setSocialForm({platform: link.platform, url: link.url, icon: '', active: link.active});}}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteSocialLink(link.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* AFFILIATE TAB */}
          <TabsContent value="affiliate">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-bold mb-6 font-outfit">{editingAffiliate ? t('Modifier', 'Bearbeiten') : t('Nouveau lien', 'Neuer Link')}</h2>
                  <form onSubmit={handleAffiliateSubmit} className="space-y-4">
                    <div><Label>{t('Nom', 'Name')}</Label><Input value={affiliateForm.name} onChange={(e) => setAffiliateForm({...affiliateForm, name: e.target.value})} required /></div>
                    <div><Label>URL</Label><Input value={affiliateForm.url} onChange={(e) => setAffiliateForm({...affiliateForm, url: e.target.value})} required /></div>
                    <div><Label>{t('Image URL', 'Bild-URL')}</Label><Input value={affiliateForm.image_url} onChange={(e) => setAffiliateForm({...affiliateForm, image_url: e.target.value})} /></div>
                    <div>
                      <Label>{t('Catégorie', 'Kategorie')}</Label>
                      <Select value={affiliateForm.category} onValueChange={(val) => setAffiliateForm({...affiliateForm, category: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t('Général', 'Allgemein')}</SelectItem>
                          <SelectItem value="travel">{t('Voyage', 'Reisen')}</SelectItem>
                          <SelectItem value="tech">Tech</SelectItem>
                          <SelectItem value="books">{t('Livres', 'Bücher')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Commission</Label><Input value={affiliateForm.commission} onChange={(e) => setAffiliateForm({...affiliateForm, commission: e.target.value})} placeholder="5%, 10€..." /></div>
                    <div className="flex items-center justify-between"><Label>{t('Actif', 'Aktiv')}</Label><Switch checked={affiliateForm.active} onCheckedChange={(checked) => setAffiliateForm({...affiliateForm, active: checked})} /></div>
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1"><Save className="w-4 h-4 mr-2" />{editingAffiliate ? t('Mettre à jour', 'Aktualisieren') : t('Ajouter', 'Hinzufügen')}</Button>
                      {editingAffiliate && <Button type="button" variant="outline" onClick={() => {setAffiliateForm({ name: '', description_fr: '', description_de: '', url: '', image_url: '', category: 'general', commission: '', active: true }); setEditingAffiliate(null);}}><X className="w-4 h-4" /></Button>}
                    </div>
                  </form>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-bold mb-6 font-outfit">{t('Liens d\'affiliation', 'Affiliate-Links')} ({affiliateLinks.length})</h2>
                  {affiliateLinks.length === 0 ? (
                    <div className="text-center py-8"><DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">{t('Aucun lien', 'Keine Links')}</p></div>
                  ) : (
                    <div className="space-y-4">
                      {affiliateLinks.map((link) => (
                        <div key={link.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                          {link.image_url && <img src={link.image_url} alt={link.name} className="w-12 h-12 object-cover rounded" />}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium">{link.name}</h3>
                            <p className="text-sm text-slate-500">{link.category} {link.commission && `• ${link.commission}`}</p>
                          </div>
                          <span className="text-xs text-blue-600"><MousePointer className="w-3 h-3 inline mr-1" />{link.clicks || 0}</span>
                          <Button size="sm" variant="ghost" onClick={() => {setEditingAffiliate(link); setAffiliateForm({...link});}}><Edit className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteAffiliateLink(link.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* HERO TAB */}
          <TabsContent value="hero">
            <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
              <h2 className="text-lg font-bold mb-6 font-outfit">{t("Image d'accueil", 'Hero-Bild')}</h2>
              {heroImageInfo?.exists && (
                <div className="mb-6">
                  <div className="rounded-xl overflow-hidden border border-slate-200">
                    <img src={`${heroImageInfo.path}?t=${Date.now()}`} alt="Hero" className="w-full h-64 object-cover" />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">{heroImageInfo.filename} ({Math.round(heroImageInfo.size / 1024)} KB)</p>
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleHeroUpload} accept="image/jpeg,image/png,image/webp" className="hidden" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingHero} className="w-full">
                {uploadingHero ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {t('Choisir une image', 'Bild auswählen')}
              </Button>
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
              <h2 className="text-lg font-bold mb-6 font-outfit">{t('Paramètres', 'Einstellungen')}</h2>
              <form onSubmit={handleSettingsSubmit} className="space-y-6">
                <div className="space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h3 className="font-semibold flex items-center gap-2"><Languages className="w-5 h-5 text-blue-600" /> DeepL API</h3>
                  <div>
                    <Label>DeepL API Key</Label>
                    <Input value={settings.deepl_api_key || ''} onChange={(e) => setSettings({...settings, deepl_api_key: e.target.value})} type="password" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx:fx" />
                    <p className="text-xs text-slate-500 mt-1">{t('Obtenez votre clé sur deepl.com/pro', 'Holen Sie sich Ihren Schlüssel bei deepl.com/pro')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold">Google Analytics</h3>
                  <div>
                    <Label>Google Analytics ID</Label>
                    <Input value={settings.google_analytics_id || ''} onChange={(e) => setSettings({...settings, google_analytics_id: e.target.value})} placeholder="G-XXXXXXXXXX" />
                  </div>
                </div>
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold">{t('Informations', 'Informationen')}</h3>
                  <div><Label>{t('Nom', 'Name')}</Label><Input value={settings.owner_name || ''} onChange={(e) => setSettings({...settings, owner_name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input value={settings.owner_email || ''} onChange={(e) => setSettings({...settings, owner_email: e.target.value})} type="email" /></div>
                </div>
                <Button type="submit"><Save className="w-4 h-4 mr-2" />{t('Sauvegarder', 'Speichern')}</Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
